// ANANT SAMACHAR NETWORK (ASN) — Hyperlocal News + Ads + Reporter Affiliate Network
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Newspaper, Sparkles, Loader2, ShieldCheck, IdCard, Radio, Megaphone, FileText,
  CheckCircle2, XCircle, Clock, Download, MessageCircle, Copy, MapPin, Youtube, Users,
  Trophy, Phone, Vote, Volume2, VolumeX, Share2, Shield,
} from 'lucide-react';
import ATWalletBadge from '../../components/ATWalletBadge';
import api from '../../utils/api';

// Reusable file upload button — uploads to /api/upload/image and returns URL
async function uploadImageFile(file) {
  const form = new FormData();
  form.append('file', file);
  const token = localStorage.getItem('token');
  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload/image`, {
    method: 'POST', body: form, headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(await res.text());
  const data = await res.json();
  // Prepend host so image renders in modal + downloads
  return data.url.startsWith('http') ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
}

function UploadPickerBtn({ value, onChange, label = 'Upload', accept = 'image/*', testid }) {
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const url = await uploadImageFile(f);
      onChange(url);
      toast.success('Uploaded');
    } catch (err) { toast.error('Upload failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2">
      <label className={`text-[10px] px-2 py-1 rounded font-black cursor-pointer ${busy ? 'bg-amber-500/40 text-amber-200' : 'bg-blue-500 text-white hover:bg-blue-400'}`}>
        {busy ? '⏳ Uploading…' : `📎 ${label}`}
        <input type="file" accept={accept} onChange={onFile} className="hidden" data-testid={testid} />
      </label>
      {value && <a href={value} target="_blank" rel="noreferrer" className="text-[10px] text-blue-300 underline truncate max-w-[140px]">✓ Uploaded</a>}
    </div>
  );
}

// Provided image assets
const ASN_LOGO = 'https://customer-assets-0z36b82j.emergentagent.net/job_94259c42-41bd-46e6-b640-f415dc05ba06/artifacts/asn-logo.png';
// Fallback inline SVG-based logo shown if hosted asset unavailable
const ASN_LOGO_FALLBACK = 'data:image/svg+xml;utf8,' + encodeURIComponent(`
<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'>
  <defs>
    <linearGradient id='g' x1='0' x2='1' y1='0' y2='1'>
      <stop offset='0' stop-color='#1e3a8a'/><stop offset='0.5' stop-color='#c8a24a'/><stop offset='1' stop-color='#1e3a8a'/>
    </linearGradient>
  </defs>
  <circle cx='100' cy='100' r='95' fill='url(#g)'/>
  <text x='100' y='105' text-anchor='middle' fill='#fff' font-family='Cinzel,serif' font-weight='900' font-size='60'>ASN</text>
  <text x='100' y='140' text-anchor='middle' fill='#fef3c7' font-family='Georgia' font-size='11'>NEWS NETWORK</text>
</svg>
`);

export default function AnantSamachar() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('feed');
  const [searchParams] = useSearchParams();
  const refUsername = searchParams.get('ref') || '';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/asn/config');
      setConfig(r.data);
    } catch (e) { toast.error('Load failed'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading || !config) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }

  const rep = config.reporter || {};
  const isActive = rep.is_active_reporter;
  const kycApproved = rep.kyc_status === 'APPROVED';

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-b from-blue-950 via-slate-950 to-black text-white">
      <div className="sticky top-16 z-40 bg-slate-950 backdrop-blur-xl border-b-2 border-blue-400/60 px-4 py-2 flex items-center justify-between shadow-2xl shadow-black">
        <Link to="/" className="text-xs text-slate-400 hover:text-white">← Home</Link>
        <ATWalletBadge compact />
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* HERO */}
        <div className="relative overflow-hidden rounded-3xl border-2 border-blue-400/50 bg-gradient-to-br from-blue-950 via-indigo-900/50 to-amber-950 p-5 mb-6" data-testid="asn-hero">
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-amber-500/20 blur-3xl pointer-events-none animate-pulse"></div>
          <div className="absolute -bottom-16 -left-16 w-64 h-64 rounded-full bg-blue-500/20 blur-3xl pointer-events-none"></div>
          <div className="relative flex items-center gap-4">
            <img src={ASN_LOGO} onError={(e) => { e.currentTarget.src = ASN_LOGO_FALLBACK; }} alt="ASN" className="w-16 h-16 sm:w-20 sm:h-20 rounded-full border-2 border-amber-400 shadow-xl shadow-amber-500/50" />
            <div className="flex-1">
              <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/25 border border-amber-400/40 text-[9px] font-black tracking-widest text-amber-200 mb-1">
                <Sparkles className="w-2.5 h-2.5" /> TRUTH · CLARITY · INFINITE
              </div>
              <h1 className="text-2xl sm:text-3xl font-black bg-gradient-to-r from-blue-300 via-amber-200 to-yellow-300 bg-clip-text text-transparent" style={{ fontFamily: '"Cinzel", Georgia, serif' }}>
                ANANT SAMACHAR NETWORK
              </h1>
              <div className="text-xs opacity-90 mt-0.5">Be the Voice of Your City — Join ASN & Earn!</div>
            </div>
          </div>
          <div className="mt-3 flex gap-1.5 flex-wrap">
            <Chip color="amber">🎯 1000 AT Activation + 500 AT/mo</Chip>
            <Chip color="emerald">🎉 7-Level Affiliate Network · 33% Distributed</Chip>
            <Chip color="blue">🆔 Digital Reporter ID Card</Chip>
            <Chip color="rose">📢 Self-Serve Ads · 15% Reporter Commission</Chip>
          </div>
        </div>

        {/* BREAKING NEWS TICKER */}
        <TickerBar city={rep.city} />

        <div className="mb-3">
          <Link to="/asn/all" data-testid="asn-goto-all" className="block w-full text-center px-3 py-2 rounded-xl bg-gradient-to-r from-blue-500 via-red-500 to-amber-500 text-white text-xs font-black">🗞️ View All News Showcase →</Link>
        </div>

        {/* TAB SWITCHER */}
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-1 p-1 rounded-2xl border border-slate-700 bg-slate-900/70 mb-4" data-testid="asn-tabs">
          <TabBtn active={tab === 'feed'} onClick={() => setTab('feed')} data-testid="asn-tab-feed"><Newspaper className="w-3 h-3" /> Feed</TabBtn>
          <TabBtn active={tab === 'live'} onClick={() => setTab('live')} data-testid="asn-tab-live"><Radio className="w-3 h-3" /> Live TV</TabBtn>
          <TabBtn active={tab === 'leader'} onClick={() => setTab('leader')} data-testid="asn-tab-leader"><Trophy className="w-3 h-3" /> Top 10</TabBtn>
          <TabBtn active={tab === 'reporter'} onClick={() => setTab('reporter')} data-testid="asn-tab-reporter"><IdCard className="w-3 h-3" /> Reporter</TabBtn>
          <TabBtn active={tab === 'publish'} onClick={() => setTab('publish')} data-testid="asn-tab-publish"><FileText className="w-3 h-3" /> Publish</TabBtn>
          <TabBtn active={tab === 'ads'} onClick={() => setTab('ads')} data-testid="asn-tab-ads"><Megaphone className="w-3 h-3" /> Ads</TabBtn>
        </div>

        {tab === 'feed' && <FeedTab city={rep.city} />}
        {tab === 'live' && <LiveTVTab city={rep.city} />}
        {tab === 'leader' && <LeaderboardTab />}
        {tab === 'reporter' && <ReporterTab config={config} refUsername={refUsername} onReload={load} isActive={isActive} kycApproved={kycApproved} />}
        {tab === 'publish' && <PublishTab config={config} onReload={load} canPublish={isActive && kycApproved} />}
        {tab === 'ads' && <AdsTab city={rep.city} config={config} />}

        {/* LEGAL FOOTER */}
        <div className="mt-8 rounded-2xl border border-slate-800 bg-slate-900/60 p-3 text-[10px] text-slate-400 leading-relaxed">
          <b className="text-slate-200">ASN (Anant Samachar Network) Disclaimer:</b> ASN is a User-Generated Content platform. The news, views, and advertisements published on this portal are the sole responsibility of the respective reporters and advertisers. ASN does not guarantee the accuracy, reliability, or completeness of the content and is not liable for any indirect damages. Please verify information independently before use.
        </div>
      </div>
    </div>
  );
}

const Chip = ({ children, color = 'blue' }) => {
  const map = { blue: 'bg-blue-500/15 text-blue-200 border-blue-400/30', amber: 'bg-amber-500/15 text-amber-200 border-amber-400/30', emerald: 'bg-emerald-500/15 text-emerald-200 border-emerald-400/30', rose: 'bg-rose-500/15 text-rose-200 border-rose-400/30' };
  return <span className={`text-[10px] px-2 py-0.5 rounded-full border font-black ${map[color]}`}>{children}</span>;
};

const TabBtn = ({ active, onClick, children, ...rest }) => (
  <button onClick={onClick} {...rest} className={`px-2 py-2 rounded-xl text-[11px] font-black transition-all flex items-center justify-center gap-1 ${active ? 'bg-gradient-to-r from-blue-500 to-amber-500 text-black shadow-lg' : 'text-slate-400 hover:text-white'}`}>{children}</button>
);

// ============ TICKER (with Web Push Notifications) ============
function TickerBar({ city }) {
  const [tickers, setTickers] = useState([]);
  const seenRef = useRef(new Set());
  const [notifOn, setNotifOn] = useState(typeof Notification !== 'undefined' && Notification.permission === 'granted');

  const requestNotif = async () => {
    if (typeof Notification === 'undefined') { toast.error('Browser notifications not supported'); return; }
    if (Notification.permission === 'granted') { setNotifOn(true); return; }
    if (Notification.permission === 'denied') { toast.error('Notifications blocked in browser settings'); return; }
    const p = await Notification.requestPermission();
    if (p === 'granted') { setNotifOn(true); toast.success('🔔 Alerts enabled'); }
    else toast.error('Notifications denied');
  };

  useEffect(() => {
    let mounted = true;
    const poll = async () => {
      try {
        const r = await api.get('/asn/tickers', { params: city ? { city } : {} });
        if (!mounted) return;
        const rows = r.data?.tickers || [];
        setTickers(rows);
        // Fire browser notification for new tickers only (skip on first load)
        if (seenRef.current.size > 0 && notifOn && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
          rows.forEach((t) => {
            if (!seenRef.current.has(t.id)) {
              try {
                const n = new Notification('⚡ ASN Breaking News', { body: t.text, icon: '/favicon.ico', tag: t.id });
                setTimeout(() => n.close?.(), 8000);
              } catch { /* ignore */ }
            }
          });
        }
        rows.forEach((t) => seenRef.current.add(t.id));
      } catch { /* silent */ }
    };
    poll();
    const iv = setInterval(poll, 45000);   // poll every 45 sec — 0 cost
    return () => { mounted = false; clearInterval(iv); };
  }, [city, notifOn]);

  if (tickers.length === 0) return null;
  const text = tickers.map((t) => `⚡ ${t.text}`).join('     ·     ');
  return (
    <div className="rounded-lg bg-red-600 border-2 border-red-400 mb-4 overflow-hidden" data-testid="asn-ticker">
      <div className="flex items-center gap-2 px-2 py-1.5">
        <span className="text-[10px] font-black bg-white text-red-600 px-2 py-0.5 rounded animate-pulse flex-shrink-0"><Radio className="w-3 h-3 inline mr-0.5" /> BREAKING</span>
        <div className="flex-1 overflow-hidden">
          <div className="whitespace-nowrap animate-marquee text-xs font-black" style={{ animation: 'asn-marquee 30s linear infinite' }}>{text}   ·   {text}</div>
        </div>
        {!notifOn && (
          <button onClick={requestNotif} data-testid="asn-notif-enable" className="text-[9px] px-2 py-0.5 rounded bg-white text-red-600 font-black flex-shrink-0" title="Enable browser alerts for breaking news">🔔 Alerts</button>
        )}
        {notifOn && <span className="text-[9px] text-white opacity-80 flex-shrink-0">🔔</span>}
      </div>
      <style>{`@keyframes asn-marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }`}</style>
    </div>
  );
}

// ============ FEED ============
function FeedTab({ city }) {
  const [items, setItems] = useState([]);
  const [ads, setAds] = useState([]);
  const [polls, setPolls] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filterCity, setFilterCity] = useState(city || '');
  const [filterType, setFilterType] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const loadAll = useCallback(async () => {
    try {
      const params = {};
      if (filterCity) params.city = filterCity;
      if (filterType) params.content_type = filterType;
      if (filterCat) params.category = filterCat;
      const [c, a, p, cats] = await Promise.all([
        api.get('/asn/content', { params }),
        api.get('/asn/ads/live', { params: filterCity ? { city: filterCity } : {} }),
        api.get('/asn/polls', { params: filterCity ? { city: filterCity } : {} }),
        api.get('/asn/categories'),
      ]);
      setItems(c.data?.content || []);
      setAds(a.data?.ads || []);
      setPolls(p.data?.polls || []);
      setCategories(cats.data?.categories || []);
    } catch { /* silent */ }
  }, [filterCity, filterType, filterCat]);
  useEffect(() => { loadAll(); }, [loadAll]);

  return (
    <div data-testid="asn-feed">
      {/* Header banner ad slot */}
      {ads.filter((a) => a.slot === 'header')[0] && (
        <AdBanner ad={ads.filter((a) => a.slot === 'header')[0]} className="mb-4" />
      )}

      <div className="flex gap-2 mb-3 flex-wrap">
        <input placeholder="Filter city (e.g. Mumbai)" value={filterCity} onChange={(e) => setFilterCity(e.target.value)} data-testid="asn-feed-city" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} data-testid="asn-feed-type" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs">
          <option value="">All Types</option>
          <option value="PACK">Pack (Article)</option><option value="POST">Post</option><option value="VIDEO">Video</option>
        </select>
        <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)} data-testid="asn-feed-cat" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs">
          <option value="">All Categories</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {/* Polls */}
      {polls.length > 0 && (
        <div className="space-y-2 mb-3">
          {polls.slice(0, 2).map((p) => <PollCard key={p.id} poll={p} onVote={loadAll} />)}
        </div>
      )}

      {items.length === 0 ? (
        <div className="rounded-xl border border-slate-700 bg-slate-900/60 p-8 text-center text-sm opacity-70">
          <Newspaper className="w-12 h-12 mx-auto opacity-30 mb-2" />
          Koi news nahi mili. Reporter ban ke apna sheher ki khabar sabse pehle dalo!
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((n, i) => (
            <React.Fragment key={n.id}>
              <NewsCard n={n} />
              {(i + 1) % 3 === 0 && ads.filter((a) => a.slot === 'in_feed')[Math.floor(i / 3) % Math.max(1, ads.filter((a) => a.slot === 'in_feed').length)] && (
                <AdBanner ad={ads.filter((a) => a.slot === 'in_feed')[Math.floor(i / 3) % ads.filter((a) => a.slot === 'in_feed').length]} />
              )}
            </React.Fragment>
          ))}
        </div>
      )}

      {ads.filter((a) => a.slot === 'footer')[0] && (
        <AdBanner ad={ads.filter((a) => a.slot === 'footer')[0]} className="mt-4" />
      )}
    </div>
  );
}

// ============ COUNTDOWN TIMER ============
function useCountdown(expiryIso) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);
  if (!expiryIso) return null;
  const diff = new Date(expiryIso).getTime() - now;
  if (diff <= 0) return { expired: true, text: 'Expired' };
  const d = Math.floor(diff / (1000 * 60 * 60 * 24));
  const h = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  return { expired: false, text: `${d}d ${h}h ${m}m`, urgent: diff < 24 * 60 * 60 * 1000 };
}

function CountdownBadge({ expiry, testid }) {
  const cd = useCountdown(expiry);
  if (!cd || cd.expired) return null;
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-full font-black ${cd.urgent ? 'bg-red-500 text-white animate-pulse' : 'bg-amber-500/25 text-amber-200 border border-amber-400/40'}`} title={`Expires in ${cd.text}`}>
      <Clock className="w-2.5 h-2.5" /> {cd.text}
    </span>
  );
}

// ============ AD BANNER (with countdown) ============
function AdBanner({ ad, className = '' }) {
  return (
    <div className={`relative ${className}`}>
      <a href={ad.click_url || '#'} target="_blank" rel="noreferrer" className="block group">
        <div className="relative overflow-hidden rounded-lg border-2 border-amber-400/40 hover:border-amber-300 transition-all">
          <img src={ad.banner_url} alt="Ad" className="w-full group-hover:scale-105 transition-transform duration-500" />
          <div className="absolute top-1 left-1"><CountdownBadge expiry={ad.display_expiry || ad.expires_at} testid={`ad-countdown-${ad.id}`} /></div>
          <div className="absolute top-1 right-1 text-[8px] px-1.5 py-0.5 rounded-full bg-black/60 text-amber-200 font-black">Sponsored</div>
        </div>
      </a>
      <div className="text-[9px] opacity-60 text-right">{ad.target_city} · Plan {ad.plan || '—'}</div>
    </div>
  );
}

// ============ POLL CARD ============
function PollCard({ poll, onVote }) {
  const [busy, setBusy] = useState(false);
  const vote = async (idx) => {
    if (poll.my_vote !== null && poll.my_vote !== undefined) { toast.info('Already voted'); return; }
    setBusy(true);
    try { await api.post(`/asn/polls/${poll.id}/vote`, { option_index: idx }); toast.success('Vote counted 🗳️'); onVote?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  const total = poll.total_votes || 0;
  const results = poll.results || {};
  return (
    <div className={`rounded-xl border-2 p-3 ${poll.sponsored ? 'border-amber-400 bg-gradient-to-br from-amber-950/50 to-slate-900 shadow-lg shadow-amber-500/20' : 'border-purple-400/40 bg-gradient-to-br from-purple-900/40 to-slate-900'}`} data-testid={`asn-poll-${poll.id}`}>
      {poll.sponsored && (
        <a href={poll.brand_click_url || '#'} target="_blank" rel="noreferrer" className="flex items-center gap-2 mb-2 pb-2 border-b border-amber-400/30">
          {poll.brand_logo_url && <img src={poll.brand_logo_url} alt={poll.brand_name} className="w-8 h-8 rounded object-cover border border-amber-400" onError={(e) => e.currentTarget.style.display = 'none'} />}
          <div className="flex-1">
            <div className="text-[9px] uppercase tracking-widest text-amber-300 font-black">💎 Sponsored Poll · By {poll.brand_name}</div>
            <div className="text-[9px] opacity-70">Click to know more</div>
          </div>
        </a>
      )}
      <div className="flex items-center gap-2 mb-2">
        <span className={`text-[9px] px-1.5 py-0.5 rounded font-black flex items-center gap-1 ${poll.sponsored ? 'bg-amber-500 text-black' : 'bg-purple-500 text-white'}`}><Vote className="w-2.5 h-2.5" /> {poll.sponsored ? 'BRAND POLL' : 'JANATA KA POLL'}</span>
        <CountdownBadge expiry={poll.expires_at} />
        <span className="text-[10px] opacity-70 ml-auto">{total} votes</span>
      </div>
      <div className="text-sm font-black mb-2">{poll.question}</div>
      <div className="space-y-1.5">
        {poll.options.map((opt, i) => {
          const c = results[String(i)] || 0;
          const pct = total > 0 ? Math.round((c / total) * 100) : 0;
          const voted = poll.my_vote === i;
          const canVote = poll.my_vote === null || poll.my_vote === undefined;
          return (
            <button key={i} onClick={() => canVote && vote(i)} disabled={busy || !canVote} data-testid={`poll-opt-${poll.id}-${i}`}
              className={`w-full text-left relative overflow-hidden rounded-lg border transition-all ${voted ? (poll.sponsored ? 'border-amber-300 bg-amber-500/25' : 'border-purple-300 bg-purple-500/25') : canVote ? 'border-slate-700 hover:border-purple-400 bg-slate-800/60' : 'border-slate-700 bg-slate-800/40'}`}>
              <div className={`absolute inset-0 pointer-events-none transition-all ${poll.sponsored ? 'bg-gradient-to-r from-amber-500/30 to-yellow-500/20' : 'bg-gradient-to-r from-purple-500/30 to-fuchsia-500/20'}`} style={{ width: `${pct}%` }}></div>
              <div className="relative px-3 py-1.5 flex items-center justify-between text-xs">
                <span className="font-black">{voted && '✓ '}{opt}</span>
                <span className="text-[10px] font-mono opacity-80">{pct}% · {c}</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ============ TTS (Multi-language) ============
function useTTS() {
  const [speaking, setSpeaking] = useState(false);
  const [voices, setVoices] = useState([]);
  useEffect(() => {
    const load = () => setVoices(window.speechSynthesis?.getVoices() || []);
    load();
    window.speechSynthesis && (window.speechSynthesis.onvoiceschanged = load);
  }, []);
  const speak = (text, lang = 'hi-IN') => {
    if (!window.speechSynthesis) { toast.error('TTS not supported'); return; }
    window.speechSynthesis.cancel();
    const u = new window.SpeechSynthesisUtterance(text);
    u.lang = lang;
    const v = voices.find((x) => x.lang === lang) || voices.find((x) => x.lang?.startsWith(lang.split('-')[0]));
    if (v) u.voice = v;
    u.rate = 0.95;
    u.onend = () => setSpeaking(false);
    u.onerror = () => setSpeaking(false);
    setSpeaking(true);
    window.speechSynthesis.speak(u);
  };
  const stop = () => { window.speechSynthesis?.cancel(); setSpeaking(false); };
  return { speak, stop, speaking };
}

function NewsCard({ n }) {
  const { speak, stop, speaking } = useTTS();
  const [lang, setLang] = useState('hi-IN');
  const [sharing, setSharing] = useState(false);
  const cardRef = React.useRef(null);
  const listenText = `${n.title}. ${n.body || ''}`;

  const shareToWhatsApp = async () => {
    setSharing(true);
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(cardRef.current, { scale: 2, backgroundColor: '#0f172a', useCORS: true, allowTaint: true });
      canvas.toBlob(async (blob) => {
        if (!blob) { toast.error('Image failed'); setSharing(false); return; }
        const file = new File([blob], `asn-${n.id}.png`, { type: 'image/png' });
        const shareText = `📰 *${n.title}*\n\n${(n.body || '').slice(0, 300)}${(n.body || '').length > 300 ? '…' : ''}\n\n— ANANT SAMACHAR NETWORK\nhttps://dvc-shop-build.emergent.host/asn/news/${n.id}`;
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          try { await navigator.share({ title: n.title, text: shareText, files: [file] }); }
          catch { /* user cancel */ }
        } else {
          // Fallback: download image + open wa.me with text
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a'); a.href = url; a.download = `asn-${n.id}.png`; a.click();
          URL.revokeObjectURL(url);
          window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank');
        }
        setSharing(false);
      }, 'image/png');
    } catch (e) { toast.error('Share failed'); setSharing(false); }
  };

  return (
    <div className="rounded-xl border border-slate-700 bg-slate-900/60 overflow-hidden hover:border-blue-400/50 hover:shadow-lg hover:shadow-blue-500/20 transition-all group" data-testid={`asn-news-${n.id}`}>
      <div ref={cardRef} className="asn-shareable">
        {n.media_url && <img src={n.media_url} alt={n.title} className="w-full max-h-64 object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />}
        {n.youtube_url && (
          <div className="aspect-video bg-black">
            <iframe title={n.title} src={n.youtube_url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')} allowFullScreen className="w-full h-full" />
          </div>
        )}
        <div className="p-3">
          <div className="flex items-center gap-2 text-[10px] opacity-70 mb-1 flex-wrap">
            {n.is_pinned && <span className="px-1.5 py-0.5 rounded bg-red-500 text-white font-black flex items-center gap-1 animate-pulse">📌 PINNED</span>}
            <span className="px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300 font-black">{n.content_type}</span>
            <MapPin className="w-3 h-3" /> {n.city}
            <span>· {n.category}</span>
            <CountdownBadge expiry={n.display_expiry} testid={`news-countdown-${n.id}`} />
            <span className="ml-auto">{new Date(n.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
          </div>
          <div className="text-sm font-black">{n.title}</div>
          {n.body && <div className="text-xs opacity-90 mt-1 whitespace-pre-wrap line-clamp-6">{n.body}</div>}
          <div className="text-[10px] opacity-60 mt-2">— {n.reporter_id || 'ASN Editor'} · @{n.username}</div>
        </div>
      </div>
      <div className="px-3 pb-2 flex flex-wrap gap-1.5 items-center border-t border-slate-800 pt-2">
        <Link to={`/asn/news/${n.id}`} className="text-[10px] px-2 py-1 rounded-lg bg-blue-500/20 text-blue-300 font-black hover:bg-blue-500 hover:text-white transition-all">📖 Read More</Link>
        <select value={lang} onChange={(e) => setLang(e.target.value)} data-testid={`news-lang-${n.id}`} className="text-[10px] px-1.5 py-1 rounded bg-slate-800 border border-slate-700">
          <option value="hi-IN">🇮🇳 Hindi</option>
          <option value="en-IN">🇬🇧 English</option>
          <option value="mr-IN">मराठी</option>
          <option value="gu-IN">ગુજરાતી</option>
          <option value="bn-IN">বাংলা</option>
          <option value="ta-IN">தமிழ்</option>
          <option value="te-IN">తెలుగు</option>
          <option value="kn-IN">ಕನ್ನಡ</option>
          <option value="ml-IN">മലയാളം</option>
          <option value="pa-IN">ਪੰਜਾਬੀ</option>
        </select>
        {speaking ? (
          <button onClick={stop} data-testid={`news-stop-${n.id}`} className="text-[10px] px-2 py-1 rounded-lg bg-red-500/20 text-red-300 font-black flex items-center gap-1"><VolumeX className="w-3 h-3" /> Stop</button>
        ) : (
          <button onClick={() => speak(listenText, lang)} data-testid={`news-listen-${n.id}`} className="text-[10px] px-2 py-1 rounded-lg bg-emerald-500/20 text-emerald-300 font-black flex items-center gap-1 hover:bg-emerald-500 hover:text-black transition-all"><Volume2 className="w-3 h-3" /> Listen</button>
        )}
        <button onClick={shareToWhatsApp} disabled={sharing} data-testid={`news-share-${n.id}`} className="ml-auto text-[10px] px-2 py-1 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-black flex items-center gap-1 disabled:opacity-40 hover:shadow-lg hover:shadow-emerald-500/40 transition-all">
          <Share2 className="w-3 h-3" /> {sharing ? '…' : 'WhatsApp Status'}
        </button>
      </div>
    </div>
  );
}

// ============ REPORTER (KYC + Activate + ID Card) ============
function ReporterTab({ config, refUsername, onReload, isActive, kycApproved }) {
  const rep = config.reporter || {};
  const [form, setForm] = useState({
    full_name: rep.full_name || '', mobile: rep.mobile || '', city: rep.city || '',
    photo_url: rep.photo_url || '', kyc_doc_url: rep.kyc_doc_url || '',
    accept_terms: false, sponsor_username: refUsername || rep.sponsor_username || '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [activateAccept, setActivateAccept] = useState(false);

  const submitKyc = async () => {
    if (!form.accept_terms) { toast.error('Terms accept karo'); return; }
    setSubmitting(true);
    try {
      await api.post('/asn/kyc/submit', form);
      toast.success('KYC submitted. Admin review karega');
      await onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };
  const activate = async () => {
    if (!activateAccept) { toast.error('T&C accept karo pehle'); return; }
    if (!window.confirm(`1000 AT deduct hoga — activate ASN Reporter?`)) return;
    try {
      const r = await api.post('/asn/activate');
      toast.success(r.data.message);
      await onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const renew = async () => {
    if (!window.confirm(`500 AT renewal — proceed?`)) return;
    try {
      const r = await api.post('/asn/renew');
      toast.success(`Renewed till ${new Date(r.data.expires_at).toLocaleDateString()}`);
      await onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const kycBadge = { NOT_SUBMITTED: 'bg-slate-500/25 text-slate-300', PENDING: 'bg-amber-500/25 text-amber-300', APPROVED: 'bg-emerald-500/25 text-emerald-300', REJECTED: 'bg-red-500/25 text-red-300' };

  return (
    <div className="space-y-4" data-testid="asn-reporter-tab">
      {/* KYC status */}
      <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-4">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="text-xs uppercase tracking-widest text-blue-300 font-black">🪪 KYC Status</div>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-black uppercase ${kycBadge[rep.kyc_status] || kycBadge.NOT_SUBMITTED}`}>{rep.kyc_status}</span>
        </div>
        {rep.kyc_notes && <div className="text-[10px] opacity-70 mb-2">Admin note: {rep.kyc_notes}</div>}

        {rep.kyc_status !== 'APPROVED' && (
          <div className="space-y-2">
            <input placeholder="Full Name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="asn-kyc-name" className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            <div className="grid grid-cols-2 gap-2">
              <input placeholder="Mobile (10 digit)" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="asn-kyc-mobile" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
              <input placeholder="City / District" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="asn-kyc-city" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            </div>
            <div className="flex items-center gap-2">
              <input placeholder="Profile Photo URL (or upload →)" value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} data-testid="asn-kyc-photo" className="flex-1 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
              <UploadPickerBtn value={form.photo_url} onChange={(url) => setForm({ ...form, photo_url: url })} label="Photo" testid="asn-kyc-photo-upload" />
            </div>
            <div className="flex items-center gap-2">
              <input placeholder="KYC Document URL (or upload →)" value={form.kyc_doc_url} onChange={(e) => setForm({ ...form, kyc_doc_url: e.target.value })} data-testid="asn-kyc-doc" className="flex-1 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
              <UploadPickerBtn value={form.kyc_doc_url} onChange={(url) => setForm({ ...form, kyc_doc_url: url })} label="KYC" accept="image/*,application/pdf" testid="asn-kyc-doc-upload" />
            </div>
            <KycOcrHelper form={form} setForm={setForm} />
            <input placeholder="Sponsor Username (referral · optional)" value={form.sponsor_username} onChange={(e) => setForm({ ...form, sponsor_username: e.target.value })} data-testid="asn-kyc-sponsor" className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" disabled={!!rep.sponsor_id} />
            <label className="flex items-start gap-2 text-[10px] cursor-pointer opacity-90">
              <input type="checkbox" checked={form.accept_terms} onChange={(e) => setForm({ ...form, accept_terms: e.target.checked })} data-testid="asn-kyc-tc" className="mt-0.5" />
              <span>I accept ASN Terms & Conditions and Disclaimer. Content posted by me is my sole responsibility.</span>
            </label>
            <button onClick={submitKyc} disabled={submitting} data-testid="asn-kyc-submit" className="w-full px-3 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-sm font-black disabled:opacity-40">
              {submitting ? 'Submitting…' : 'Submit KYC for Review'}
            </button>
          </div>
        )}
      </div>

      {/* Activation / Renew */}
      {kycApproved && (
        <div className="rounded-2xl border-2 border-amber-400/50 bg-gradient-to-br from-amber-950/40 to-slate-900 p-4">
          <div className="text-xs uppercase tracking-widest text-amber-300 font-black mb-2">💰 Subscription</div>
          {!rep.activation_paid ? (
            <>
              <label className="flex items-start gap-2 text-[10px] cursor-pointer mb-2 opacity-90">
                <input type="checkbox" checked={activateAccept} onChange={(e) => setActivateAccept(e.target.checked)} data-testid="asn-activate-tc" className="mt-0.5" />
                <span>I accept the Activation Fee terms — 1000 AT will be non-refundable, distributed to my Affiliate Network upline (33%) and Admin (67%).</span>
              </label>
              <button onClick={activate} disabled={!activateAccept} data-testid="asn-activate" className="w-full px-3 py-3 rounded-xl bg-gradient-to-r from-amber-400 via-orange-400 to-red-500 text-black text-sm font-black disabled:opacity-40">
                🚀 Activate Reporter · 1000 AT
              </button>
            </>
          ) : (
            <>
              <div className="text-xs mb-2">Active till: <b>{rep.subscription_expires_at ? new Date(rep.subscription_expires_at).toLocaleDateString('en-IN') : '—'}</b></div>
              <button onClick={renew} data-testid="asn-renew" className="w-full px-3 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-black text-sm font-black">
                🔁 Renew · 500 AT
              </button>
            </>
          )}
        </div>
      )}

      {/* Affiliate Network info */}
      <div className="rounded-2xl border border-emerald-400/30 bg-emerald-500/5 p-3">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
          <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-black">🎯 7-Level Affiliate Network · 33% Distributed</div>
          <div className="text-[10px]">Active Directs: <b className="text-emerald-300">{rep.active_directs || 0}</b></div>
        </div>
        <div className="grid grid-cols-7 gap-1 text-[9px] text-center">
          {config.mlm_levels.map((l) => {
            const unlocked = (rep.active_directs || 0) >= l.unlock_directs;
            return (
              <div key={l.level} className={`rounded p-1 border ${unlocked ? 'border-emerald-400 bg-emerald-500/15 text-emerald-200' : 'border-slate-700 bg-slate-800/40 text-slate-500'}`}>
                <div className="font-black">L{l.level}</div>
                <div>{l.pct}%</div>
                <div className="text-[8px] opacity-70">{unlocked ? '✓' : `${l.unlock_directs}d`}</div>
              </div>
            );
          })}
        </div>
        <div className="text-[10px] text-emerald-300 mt-2">
          Refer link: <code className="text-amber-200">{window.location.origin}/asn?ref={rep.username}</code>
          <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/asn?ref=${rep.username}`); toast.success('Copied'); }} className="ml-2 px-1.5 py-0.5 rounded bg-white/10 text-[9px]"><Copy className="w-2.5 h-2.5 inline" /></button>
        </div>
      </div>

      {/* Trust Score + Ad Earnings + Weekly Payout */}
      <TrustScoreCard rep={rep} threshold={config.trust_score_threshold || 5} />
      <ReporterAdEarnings />
      <WeeklyPayoutCard rep={rep} />
      <HelplinesCard city={rep.city} />

      {/* Digital ID Card */}
      {rep.id_card_unlocked ? <DigitalIDCard rep={rep} /> : (
        <div className="rounded-2xl border-2 border-dashed border-slate-600 bg-slate-900/40 p-6 text-center" data-testid="asn-id-locked">
          <IdCard className="w-12 h-12 mx-auto opacity-30 mb-2" />
          <div className="text-sm font-black opacity-80">🔒 Digital Reporter ID Card LOCKED</div>
          <div className="text-[11px] opacity-70 mt-1">Unlocks after: {kycApproved ? '✅' : '❌'} KYC approval + {isActive ? '✅' : '❌'} Active subscription</div>
        </div>
      )}
    </div>
  );
}

// ============ DIGITAL ID CARD (premium press credential) ============
function DigitalIDCard({ rep }) {
  const downloadPNG = async () => {
    try {
      const html2canvas = (await import('html2canvas')).default;
      const node = document.getElementById('asn-id-card');
      const canvas = await html2canvas(node, { scale: 3, backgroundColor: null, useCORS: true, allowTaint: true });
      const link = document.createElement('a');
      link.download = `ASN-ID-${rep.reporter_id.replace(/[/]/g, '-')}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('ID Card downloaded');
    } catch { toast.error('Download failed'); }
  };
  const qrData = `${window.location.origin}/asn/verify/${encodeURIComponent(rep.reporter_id || '')}`;
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=140x140&data=${encodeURIComponent(qrData)}`;

  return (
    <div className="space-y-2" data-testid="asn-id-card-wrap">
      <div className="mx-auto max-w-[360px] p-2">
        <div id="asn-id-card" className="relative rounded-3xl overflow-hidden" style={{
          background: 'linear-gradient(180deg, #0f1e4e 0%, #142b6b 50%, #0f1e4e 100%)',
          boxShadow: '0 25px 50px rgba(0,0,0,0.6), inset 0 0 0 2px #c8a24a',
        }}>
          {/* Metallic border frame */}
          <div className="p-1" style={{ background: 'linear-gradient(135deg, #d4a86a, #f0dc9b, #a67c3a, #d4a86a)' }}>
            <div className="rounded-2xl p-4" style={{ background: 'linear-gradient(180deg, #0a1440 0%, #142f7a 100%)' }}>
              {/* Lanyard clip */}
              <div className="mx-auto -mt-6 mb-2 w-16 h-4 rounded-b-lg" style={{ background: 'linear-gradient(180deg, #1e40af, #1e3a8a)', boxShadow: '0 2px 6px rgba(0,0,0,0.4)' }}></div>

              {/* Header — logo + name */}
              <div className="flex items-center gap-2 justify-center mb-3">
                <img src={ASN_LOGO} onError={(e) => { e.currentTarget.src = ASN_LOGO_FALLBACK; }} alt="ASN" className="w-12 h-12 rounded-full border-2 border-amber-400" />
                <div>
                  <div className="text-lg font-black text-amber-300" style={{ fontFamily: '"Cinzel", Georgia, serif', textShadow: '0 1px 0 rgba(0,0,0,0.6)' }}>ASN</div>
                  <div className="text-[9px] tracking-widest text-amber-200/90" style={{ fontFamily: 'Georgia,serif' }}>ANANT SAMACHAR NETWORK</div>
                </div>
              </div>

              {/* Photo */}
              <div className="mx-auto w-28 h-28 rounded-xl overflow-hidden border-2 border-amber-400 shadow-lg" style={{ background: '#0f1e4e' }}>
                {rep.photo_url ? <img src={rep.photo_url} alt={rep.full_name} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : <div className="w-full h-full flex items-center justify-center text-4xl opacity-40">👤</div>}
              </div>

              {/* Name banner */}
              <div className="mx-auto mt-3 py-1 px-4 text-center" style={{ background: 'linear-gradient(90deg, #b8860b, #ffd75a, #b8860b)', clipPath: 'polygon(2% 0, 98% 0, 100% 50%, 98% 100%, 2% 100%, 0% 50%)' }}>
                <div className="text-lg font-black text-blue-950" style={{ fontFamily: '"Cinzel", Georgia, serif', textShadow: '0 1px 0 rgba(255,255,255,0.35)' }}>{(rep.full_name || 'REPORTER').toUpperCase()}</div>
              </div>
              <div className="text-center text-[10px] text-amber-100 font-black mt-1 tracking-widest">DESIGNATION: <span className="text-amber-300">{(rep.designation || 'AUTHORIZED NEWS REPORTER').toUpperCase()}</span></div>

              {/* Details rows */}
              <div className="mt-3 space-y-1 text-[11px]" style={{ color: '#e5edff' }}>
                <div className="flex justify-between px-2 py-1 rounded" style={{ background: 'rgba(255,255,255,0.05)' }}><span className="opacity-70">MOB.:</span><span className="font-mono">+91 {rep.mobile || '—'}</span></div>
                <div className="flex justify-between px-2 py-1 rounded" style={{ background: 'rgba(255,255,255,0.05)' }}><span className="opacity-70">CITY:</span><span>{(rep.city || '—').toUpperCase()}</span></div>
              </div>

              {/* QR + ID */}
              <div className="mt-3 grid grid-cols-[100px_1fr] gap-2 items-center bg-white p-2 rounded-lg">
                <img src={qrSrc} alt="QR" className="w-24 h-24" />
                <div>
                  <div className="text-[10px] font-black text-blue-900">{rep.reporter_id}</div>
                  <div className="text-[9px] text-blue-800/80 mt-0.5">VALID TILL: <b>{rep.subscription_expires_at ? new Date(rep.subscription_expires_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase() : '—'}</b></div>
                  <div className="text-[9px] text-amber-700 font-black mt-1">TRUTH. CLARITY. INFINITE.</div>
                </div>
              </div>

              {/* Verified badge */}
              <div className="mt-2 flex items-center justify-center gap-1 text-[10px] text-emerald-300 font-black">
                <CheckCircle2 className="w-3 h-3" /> VERIFIED ACTIVE MEMBER
              </div>

              {/* Watermark repeat */}
              <div className="text-[7px] text-center text-amber-500/40 tracking-[0.4em] mt-2">ANANT SAMACHAR NETWORK · ANANT SAMACHAR NETWORK</div>
            </div>
          </div>
        </div>
      </div>
      <button onClick={downloadPNG} data-testid="asn-id-download" className="w-full max-w-[360px] mx-auto flex px-3 py-2 rounded-lg bg-gradient-to-r from-amber-400 to-yellow-300 text-black text-sm font-black items-center justify-center gap-1"><Download className="w-4 h-4" /> Download High-Res PNG</button>
    </div>
  );
}

// ============ PUBLISH ============
function PublishTab({ onReload, canPublish, config }) {
  const [form, setForm] = useState({ content_type: 'POST', title: '', body: '', media_url: '', youtube_url: '', city: config?.reporter?.city || '', category: 'General', accept_disclaimer: false });
  const [mine, setMine] = useState([]);
  const [categories, setCategories] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [showNewsStudio, setShowNewsStudio] = useState(false);
  const loadMine = useCallback(async () => {
    try { const r = await api.get('/asn/content/mine'); setMine(r.data?.content || []); } catch { /* silent */ }
  }, []);
  useEffect(() => { loadMine(); api.get('/asn/categories').then((r) => setCategories(r.data?.categories || [])).catch(() => {}); }, [loadMine]);
  const submit = async () => {
    if (!canPublish) { toast.error('Active + KYC-approved reporter required'); return; }
    if (!form.accept_disclaimer) { toast.error('Disclaimer accept karo'); return; }
    setSubmitting(true);
    try {
      await api.post('/asn/content', form);
      toast.success('Submitted for admin review');
      setForm({ ...form, title: '', body: '', media_url: '', youtube_url: '', accept_disclaimer: false });
      loadMine();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };
  const badge = { PENDING: 'bg-amber-500/25 text-amber-300', APPROVED: 'bg-emerald-500/25 text-emerald-300', REJECTED: 'bg-red-500/25 text-red-300' };

  return (
    <div className="space-y-4" data-testid="asn-publish-tab">
      <div className="rounded-2xl border border-blue-400/30 bg-blue-500/5 p-4 space-y-2">
        <div className="text-xs uppercase tracking-widest text-blue-300 font-black mb-1">✍️ Create News / Upload Post</div>
        {!canPublish && <div className="text-[10px] text-amber-300 mb-1">⚠️ KYC approval + active subscription zaroori hai. My Reporter tab mein activate karo.</div>}
        <div className="grid grid-cols-2 gap-2">
          <select value={form.content_type} onChange={(e) => setForm({ ...form, content_type: e.target.value })} data-testid="pub-type" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs">
            <option value="POST">📝 Post (Short)</option><option value="PACK">📰 Pack (Article)</option><option value="VIDEO">🎥 Video (YouTube)</option>
          </select>
          <input placeholder="City / District" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="pub-city" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
        </div>
        <input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="pub-title" className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
        <textarea placeholder="News body (max 8000 chars)" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="pub-body" rows={6} className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs resize-none" />
        <div className="grid grid-cols-1 gap-2">
          <div className="flex items-center gap-2">
            <input placeholder="Image URL (or upload →)" value={form.media_url} onChange={(e) => setForm({ ...form, media_url: e.target.value })} data-testid="pub-media" className="flex-1 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            <UploadPickerBtn value={form.media_url} onChange={(url) => setForm({ ...form, media_url: url })} label="Image" testid="pub-media-upload" />
            <button onClick={() => setShowNewsStudio(true)} data-testid="pub-news-studio" className="px-2 py-1.5 rounded bg-gradient-to-r from-fuchsia-500 to-orange-500 text-white text-[10px] font-black shrink-0">🎬 15 Cinematic Templates</button>
          </div>
          <input placeholder="YouTube URL (for VIDEO type)" value={form.youtube_url} onChange={(e) => setForm({ ...form, youtube_url: e.target.value })} data-testid="pub-yt" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
        </div>
        <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs">
          {(categories.length ? categories : ['General', 'Politics', 'Crime', 'Sports', 'Business', 'Entertainment', 'Education', 'Health', 'Weather']).map((c) => <option key={c}>{c}</option>)}
        </select>
        <label className="flex items-start gap-2 text-[10px] cursor-pointer">
          <input type="checkbox" checked={form.accept_disclaimer} onChange={(e) => setForm({ ...form, accept_disclaimer: e.target.checked })} data-testid="pub-disc" className="mt-0.5" />
          <span>I am solely responsible for this news content and adhere to ASN guidelines.</span>
        </label>
        <button onClick={submit} disabled={!canPublish || submitting} data-testid="pub-submit" className="w-full px-3 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-amber-500 text-black text-sm font-black disabled:opacity-40">
          {submitting ? 'Submitting…' : 'Submit for Review'}
        </button>
      </div>
      <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-4">
        <div className="text-xs uppercase tracking-widest text-blue-300 font-black mb-2">My Submissions ({mine.length})</div>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {mine.map((c) => (
            <div key={c.id} className="rounded-lg border border-slate-700 bg-slate-800/50 p-2">
              <div className="flex items-center justify-between mb-1 flex-wrap gap-1">
                <div className="text-xs font-black truncate flex-1 min-w-0">{c.title}</div>
                <span className={`text-[9px] px-2 py-0.5 rounded font-black ${badge[c.publish_status] || badge.PENDING}`}>{c.publish_status}</span>
              </div>
              <div className="text-[10px] opacity-70">{c.content_type} · {c.city} · {new Date(c.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
              {c.admin_notes && <div className="text-[10px] text-amber-300 mt-1">Admin: {c.admin_notes}</div>}
            </div>
          ))}
          {mine.length === 0 && <div className="text-xs opacity-60 text-center py-4">No submissions yet</div>}
        </div>
      </div>
      {showNewsStudio && <NewsCardStudioModal onExport={(url, title) => { setForm({ ...form, media_url: url, title: title || form.title }); }} onClose={() => setShowNewsStudio(false)} />}
    </div>
  );
}

// ============ NEWS CARD STUDIO — 15 Cinematic Templates for Reporters ============
const NEWS_TEMPLATES = [
  { id: 'breaking',    name: '🔴 Breaking',     kind: 'breaking',    accent: '#dc2626', bg: ['#7f1d1d','#dc2626','#000'],       fg: '#fff',    tag: 'BREAKING NEWS',       icon: '⚡' },
  { id: 'live',        name: '🎥 Live',         kind: 'live',        accent: '#ef4444', bg: ['#450a0a','#dc2626','#7f1d1d'],    fg: '#fff',    tag: 'LIVE · ON GROUND',    icon: '🔴' },
  { id: 'feature',     name: '📰 Feature',      kind: 'feature',     accent: '#0ea5e9', bg: ['#0c4a6e','#0284c7','#082f49'],    fg: '#f0f9ff', tag: 'FEATURE STORY',       icon: '📰' },
  { id: 'exclusive',   name: '💎 Exclusive',    kind: 'exclusive',   accent: '#a78bfa', bg: ['#2e1065','#7c3aed','#000'],       fg: '#f5f3ff', tag: 'EXCLUSIVE',           icon: '💎' },
  { id: 'video',       name: '▶️ Video',         kind: 'video',       accent: '#f43f5e', bg: ['#4c0519','#e11d48','#831843'],    fg: '#fff1f2', tag: 'VIDEO REPORT',        icon: '▶️' },
  { id: 'sports',      name: '⚽ Sports',        kind: 'sports',      accent: '#10b981', bg: ['#052e16','#059669','#022c22'],    fg: '#ecfdf5', tag: 'SPORTS UPDATE',       icon: '🏆' },
  { id: 'politics',    name: '🏛️ Politics',      kind: 'politics',    accent: '#f59e0b', bg: ['#78350f','#d97706','#000'],       fg: '#fffbeb', tag: 'POLITICS',            icon: '🏛️' },
  { id: 'crime',       name: '🚨 Crime',        kind: 'crime',       accent: '#eab308', bg: ['#000','#450a0a','#111']  ,        fg: '#fef3c7', tag: 'CRIME REPORT',        icon: '🚨' },
  { id: 'business',    name: '💰 Business',     kind: 'business',    accent: '#22c55e', bg: ['#052e16','#166534','#14532d'],    fg: '#f0fdf4', tag: 'BUSINESS',            icon: '📈' },
  { id: 'entertainment', name: '🎬 Cinema',     kind: 'entertainment', accent: '#ec4899', bg: ['#500724','#db2777','#000'],     fg: '#fdf2f8', tag: 'ENTERTAINMENT',       icon: '🎬' },
  { id: 'weather',     name: '⛅ Weather',      kind: 'weather',     accent: '#38bdf8', bg: ['#082f49','#0ea5e9','#164e63'],    fg: '#f0f9ff', tag: 'WEATHER ALERT',       icon: '⛅' },
  { id: 'education',   name: '🎓 Education',    kind: 'education',   accent: '#3b82f6', bg: ['#1e3a8a','#3b82f6','#1e40af'],    fg: '#eff6ff', tag: 'EDUCATION',           icon: '🎓' },
  { id: 'health',      name: '🏥 Health',       kind: 'health',      accent: '#14b8a6', bg: ['#042f2e','#0d9488','#134e4a'],    fg: '#f0fdfa', tag: 'HEALTH ADVISORY',     icon: '🏥' },
  { id: 'opinion',     name: '💭 Opinion',      kind: 'opinion',     accent: '#f97316', bg: ['#431407','#ea580c','#000'],       fg: '#fff7ed', tag: 'EDITORIAL',           icon: '💭' },
  { id: 'celebration', name: '🎉 Celebration',  kind: 'celebration', accent: '#facc15', bg: ['#7c2d12','#f59e0b','#dc2626'],    fg: '#fef3c7', tag: 'CELEBRATION',         icon: '🎉' },
];

function NewsCardStudioModal({ onExport, onClose }) {
  const [tpl, setTpl] = useState(NEWS_TEMPLATES[0]);
  const [headline, setHeadline] = useState('Aapka Headline Yahan Likhein');
  const [reporter, setReporter] = useState('');
  const [city, setCity] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const canvasRef = useRef(null);

  const draw = useCallback(async () => {
    const c = canvasRef.current; if (!c) return;
    const W = 1080, H = 1350; // Instagram-style portrait
    c.width = W; c.height = H;
    const ctx = c.getContext('2d');

    // Base gradient
    const g = ctx.createLinearGradient(0, 0, W, H);
    tpl.bg.forEach((col, i) => g.addColorStop(i / (tpl.bg.length - 1 || 1), col));
    ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);

    // Photo (background, semi-transparent)
    if (photoUrl) {
      try {
        await new Promise((resolve) => {
          const img = new Image(); img.crossOrigin = 'anonymous';
          img.onload = () => {
            const ratio = Math.max(W / img.width, H / img.height);
            const iw = img.width * ratio, ih = img.height * ratio;
            ctx.globalAlpha = 0.55;
            ctx.drawImage(img, (W - iw) / 2, (H - ih) / 2, iw, ih);
            ctx.globalAlpha = 1;
            resolve();
          };
          img.onerror = resolve;
          img.src = photoUrl;
        });
      } catch { /* ignore */ }
    }

    // Dark gradient overlay bottom
    const og = ctx.createLinearGradient(0, H * 0.35, 0, H);
    og.addColorStop(0, 'rgba(0,0,0,0)'); og.addColorStop(0.6, 'rgba(0,0,0,0.55)'); og.addColorStop(1, 'rgba(0,0,0,0.92)');
    ctx.fillStyle = og; ctx.fillRect(0, 0, W, H);

    // Diagonal accent bar
    ctx.save();
    ctx.fillStyle = tpl.accent + 'cc'; // 80% opacity
    ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(W * 0.55, 0); ctx.lineTo(W * 0.45, 140); ctx.lineTo(0, 140); ctx.closePath(); ctx.fill();
    ctx.restore();

    // Kind tag
    ctx.fillStyle = '#fff'; ctx.font = 'bold 46px "Helvetica","Arial",sans-serif';
    ctx.textAlign = 'left'; ctx.fillText(`${tpl.icon} ${tpl.tag}`, 50, 92);

    // Live pulse dot (for live/breaking)
    if (tpl.kind === 'live' || tpl.kind === 'breaking') {
      ctx.fillStyle = '#fff';
      ctx.beginPath(); ctx.arc(W - 90, 70, 18, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = '#fff'; ctx.lineWidth = 4;
      ctx.beginPath(); ctx.arc(W - 90, 70, 32, 0, Math.PI * 2); ctx.stroke();
    }

    // Headline (auto-wrap)
    ctx.fillStyle = tpl.fg;
    ctx.font = 'bold 88px "Georgia","Times New Roman",serif';
    ctx.textAlign = 'left';
    const wrap = (text, maxW) => {
      const words = String(text || '').split(' '); const lines = []; let cur = '';
      words.forEach((w) => {
        const t = cur ? cur + ' ' + w : w;
        if (ctx.measureText(t).width > maxW) { if (cur) lines.push(cur); cur = w; } else cur = t;
      });
      if (cur) lines.push(cur);
      return lines.slice(0, 5);
    };
    const lines = wrap(headline, W - 100);
    const lineH = 100; const startY = H - 400 - lines.length * lineH;
    lines.forEach((ln, i) => ctx.fillText(ln, 50, startY + i * lineH + lineH));

    // Accent underline
    ctx.fillStyle = tpl.accent; ctx.fillRect(50, H - 300, 200, 8);

    // Reporter + city
    ctx.fillStyle = tpl.fg; ctx.font = '600 40px "Helvetica","Arial",sans-serif';
    ctx.fillText(reporter ? `— ${reporter}` : '', 50, H - 220);
    ctx.font = '400 32px "Helvetica","Arial",sans-serif'; ctx.fillStyle = tpl.fg + 'cc';
    ctx.fillText(city ? `📍 ${city}` : '', 50, H - 170);

    // Date
    ctx.font = '400 28px "Helvetica","Arial",sans-serif';
    ctx.fillText(new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' }), 50, H - 130);

    // Watermark bar bottom
    ctx.fillStyle = tpl.accent; ctx.fillRect(0, H - 80, W, 80);
    ctx.fillStyle = '#fff'; ctx.textAlign = 'center';
    ctx.font = 'bold 44px "Helvetica","Arial",sans-serif';
    ctx.fillText('ANANT SAMACHAR NETWORK', W / 2, H - 30);
  }, [tpl, headline, reporter, city, photoUrl]);

  useEffect(() => { draw(); }, [draw]);

  const download = () => {
    canvasRef.current.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `asn-news-${tpl.id}-${Date.now()}.png`; a.click();
      URL.revokeObjectURL(url); toast.success('Downloaded');
    });
  };

  const useInPost = async () => {
    setBusy(true);
    try {
      const blob = await new Promise((r) => canvasRef.current.toBlob(r, 'image/png'));
      const file = new File([blob], `asn-news-${tpl.id}.png`, { type: 'image/png' });
      const url = await uploadImageFile(file);
      onExport(url, headline);
      toast.success('✓ Cinematic card set as post image');
      onClose();
    } catch { toast.error('Upload failed'); }
    finally { setBusy(false); }
  };

  const shareOnly = async () => {
    try {
      const blob = await new Promise((r) => canvasRef.current.toBlob(r, 'image/png'));
      const file = new File([blob], `asn-news-${tpl.id}.png`, { type: 'image/png' });
      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: headline, text: `${headline} — via ASN` });
      } else {
        const url = URL.createObjectURL(blob);
        window.open(`https://wa.me/?text=${encodeURIComponent(`${headline}\n— via Anant Samachar Network`)}`, '_blank');
        window.open(url, '_blank');
      }
    } catch { toast.error('Share failed'); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-start justify-center p-2 overflow-y-auto" onClick={onClose}>
      <div className="bg-slate-900 border-2 border-fuchsia-400 rounded-2xl p-3 max-w-4xl w-full my-4" onClick={(e) => e.stopPropagation()} data-testid="asn-news-studio">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-black text-fuchsia-300 uppercase tracking-widest">🎬 Cinematic News Card · 15 Templates · Zero-Cost Canvas</div>
          <button onClick={onClose} className="text-slate-400 hover:text-white text-xl">✕</button>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-1 mb-3 max-h-40 overflow-y-auto">
          {NEWS_TEMPLATES.map((t) => (
            <button key={t.id} onClick={() => setTpl(t)} data-testid={`asn-news-tpl-${t.id}`}
              className={`relative text-[10px] px-1.5 py-2 rounded-lg border-2 font-black transition-all overflow-hidden ${tpl.id === t.id ? 'border-fuchsia-400 shadow-lg shadow-fuchsia-500/50 scale-105' : 'border-slate-700 opacity-75 hover:opacity-100'}`}
              style={{ background: `linear-gradient(135deg, ${t.bg.join(', ')})`, color: t.fg, minHeight: 46 }}>
              <div className="relative z-10">{t.name}</div>
              {(t.kind === 'breaking' || t.kind === 'live') && <div className="absolute top-1 right-1 w-2 h-2 rounded-full bg-white animate-ping" />}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
          <input value={headline} onChange={(e) => setHeadline(e.target.value)} data-testid="asn-news-headline" placeholder="Main Headline (max 5 lines)" className="col-span-1 sm:col-span-2 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <input value={reporter} onChange={(e) => setReporter(e.target.value)} data-testid="asn-news-reporter" placeholder="Reporter Name (optional)" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <input value={city} onChange={(e) => setCity(e.target.value)} data-testid="asn-news-city" placeholder="City / Location" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <div className="col-span-1 sm:col-span-2 flex items-center gap-2">
            <input value={photoUrl} onChange={(e) => setPhotoUrl(e.target.value)} placeholder="Optional background photo URL (or upload →)" className="flex-1 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            <UploadPickerBtn value={photoUrl} onChange={setPhotoUrl} label="Photo" testid="asn-news-photo-up" />
          </div>
        </div>
        <div className="rounded-xl border-2 border-fuchsia-400/40 bg-black mb-3 overflow-hidden flex justify-center">
          <canvas ref={canvasRef} className="max-h-[55vh] w-auto block" style={{ imageRendering: 'auto' }} />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <button onClick={download} data-testid="asn-news-download" className="px-3 py-2 rounded-lg bg-slate-700 text-white text-xs font-black">💾 PNG</button>
          <button onClick={shareOnly} data-testid="asn-news-share" className="px-3 py-2 rounded-lg bg-green-500 text-black text-xs font-black">📤 Share</button>
          <button onClick={useInPost} disabled={busy} data-testid="asn-news-use" className="px-3 py-2 rounded-lg bg-gradient-to-r from-fuchsia-500 via-pink-500 to-orange-500 text-white text-xs font-black disabled:opacity-40">{busy ? '⏳' : '✓ Use as Post'}</button>
        </div>
      </div>
    </div>
  );
}

// ============ AD TEMPLATES STUDIO (10 canvas templates · 0-cost client-side) ============
const AD_TEMPLATES = [
  { id: 'jewelry', name: '💎 Jewelry', bg: 'linear-gradient(135deg,#7c2d12,#fbbf24,#7c2d12)', title: 'GRAND JEWELRY SALE', sub: 'Up to 40% OFF · Today Only', color: '#fef3c7' },
  { id: 'fashion', name: '👗 Fashion', bg: 'linear-gradient(135deg,#831843,#ec4899,#f9a8d4)', title: 'NEW COLLECTION', sub: 'Festive Wear · Buy 1 Get 1', color: '#fff' },
  { id: 'restaurant', name: '🍽️ Restaurant', bg: 'linear-gradient(135deg,#7f1d1d,#fbbf24)', title: 'FOODIE SPECIAL', sub: 'Family Combo @ ₹499 · Order Now', color: '#fff' },
  { id: 'realestate', name: '🏢 Real Estate', bg: 'linear-gradient(135deg,#0c4a6e,#0ea5e9,#082f49)', title: 'DREAM HOME AWAITS', sub: '2BHK from ₹35L · Ready to Move', color: '#fff' },
  { id: 'salon', name: '💇 Salon', bg: 'linear-gradient(135deg,#4c1d95,#a78bfa,#312e81)', title: 'BRIDAL PACKAGE', sub: 'Book Now · Save ₹5000', color: '#fff' },
  { id: 'medical', name: '🏥 Medical', bg: 'linear-gradient(135deg,#065f46,#10b981,#022c22)', title: 'FREE HEALTH CHECKUP', sub: 'Book Appointment Today', color: '#fff' },
  { id: 'education', name: '🎓 Education', bg: 'linear-gradient(135deg,#1e3a8a,#3b82f6,#1e40af)', title: 'ADMISSIONS OPEN', sub: 'Limited Seats · Enroll Now', color: '#fff' },
  { id: 'festival', name: '🎉 Festival', bg: 'linear-gradient(135deg,#dc2626,#f59e0b,#7c2d12)', title: 'HAPPY DIWALI', sub: 'Mega Diwali Offers', color: '#fef3c7' },
  { id: 'offer', name: '🔥 Offer', bg: 'linear-gradient(135deg,#000,#dc2626,#000)', title: 'FLAT 50% OFF', sub: 'Hurry! Last 24 Hours', color: '#fef2f2' },
  { id: 'generic', name: '⭐ Generic', bg: 'linear-gradient(135deg,#0f172a,#38bdf8,#0ea5e9)', title: 'YOUR BRAND HERE', sub: 'Special Announcement', color: '#fff' },
];

function AdStudioModal({ onExport, onClose }) {
  const [tpl, setTpl] = useState(AD_TEMPLATES[0]);
  const [title, setTitle] = useState(tpl.title);
  const [sub, setSub] = useState(tpl.sub);
  const [busy, setBusy] = useState(false);
  const canvasRef = useRef(null);

  useEffect(() => { setTitle(tpl.title); setSub(tpl.sub); }, [tpl]);

  useEffect(() => {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext('2d');
    c.width = 800; c.height = 300;
    // Background gradient
    const grad = ctx.createLinearGradient(0, 0, c.width, c.height);
    const colors = tpl.bg.match(/#[0-9a-f]+/gi) || ['#000', '#fff'];
    colors.forEach((col, i) => grad.addColorStop(i / (colors.length - 1 || 1), col));
    ctx.fillStyle = grad; ctx.fillRect(0, 0, c.width, c.height);
    // Overlay
    ctx.fillStyle = 'rgba(0,0,0,0.25)'; ctx.fillRect(0, 0, c.width, c.height);
    // Title
    ctx.fillStyle = tpl.color; ctx.textAlign = 'center';
    ctx.font = 'bold 56px Georgia, serif';
    ctx.fillText(title, c.width / 2, 130);
    ctx.font = 'bold 24px Arial'; ctx.fillStyle = tpl.color;
    ctx.fillText(sub, c.width / 2, 180);
    // Watermark
    ctx.font = '14px Arial'; ctx.fillStyle = 'rgba(255,255,255,0.5)';
    ctx.textAlign = 'right'; ctx.fillText('ASN · anantsamachar', c.width - 12, c.height - 12);
  }, [tpl, title, sub]);

  const download = () => {
    canvasRef.current.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `asn-ad-${tpl.id}.png`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Downloaded');
    });
  };

  const useInAd = async () => {
    setBusy(true);
    try {
      const blob = await new Promise((r) => canvasRef.current.toBlob(r, 'image/png'));
      const file = new File([blob], `asn-ad-${tpl.id}.png`, { type: 'image/png' });
      const form = new FormData();
      form.append('file', file);
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload/image`, {
        method: 'POST', body: form, headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      const url = data.url.startsWith('http') ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      onExport(url);
      toast.success('✓ Banner ready in Ads form');
      onClose();
    } catch { toast.error('Upload failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-3" onClick={onClose}>
      <div className="bg-slate-900 border-2 border-amber-400 rounded-2xl p-4 max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="ad-studio-modal">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs font-black text-amber-300 uppercase tracking-widest">🎨 Ad Templates Studio · Zero-Cost Canvas</div>
          <button onClick={onClose} className="text-slate-400 hover:text-white text-xl">✕</button>
        </div>
        <div className="grid grid-cols-5 gap-1.5 mb-3">
          {AD_TEMPLATES.map((t) => (
            <button key={t.id} onClick={() => setTpl(t)} data-testid={`ad-tpl-${t.id}`}
              className={`text-[10px] px-1.5 py-1.5 rounded-lg border-2 font-black transition-all ${tpl.id === t.id ? 'border-amber-400 shadow-lg shadow-amber-500/40' : 'border-slate-700 opacity-70 hover:opacity-100'}`}
              style={{ background: t.bg, color: t.color }}>{t.name}</button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2 mb-3">
          <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="ad-tpl-title" placeholder="Title" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <input value={sub} onChange={(e) => setSub(e.target.value)} data-testid="ad-tpl-sub" placeholder="Sub-title" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
        </div>
        <canvas ref={canvasRef} className="w-full rounded-xl border-2 border-amber-400/30 mb-3" />
        <div className="flex gap-2">
          <button onClick={download} className="flex-1 px-3 py-2 rounded-lg bg-slate-700 text-white text-sm font-black">💾 Download</button>
          <button onClick={useInAd} disabled={busy} data-testid="ad-tpl-use" className="flex-1 px-3 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-yellow-400 text-black text-sm font-black disabled:opacity-40">{busy ? '⏳' : '✓ Use as Banner'}</button>
        </div>
      </div>
    </div>
  );
}

// ============ SPONSORED POLL FORM ============
function SponsoredPollForm({ onDone }) {
  const [form, setForm] = useState({ question: '', options: ['', ''], city: '', brand_name: '', brand_logo_url: '', brand_click_url: '', expires_hours: 72, accept_terms: false });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const opts = form.options.filter((o) => o.trim());
    if (!form.question.trim() || opts.length < 2 || !form.brand_name.trim()) { toast.error('Question + 2 options + brand name required'); return; }
    if (!form.accept_terms) { toast.error('T&C accept karo'); return; }
    if (!window.confirm(`500 AT deduct hoga (Sponsored Poll) — proceed?`)) return;
    setBusy(true);
    try {
      await api.post('/asn/polls/sponsored', { ...form, options: opts });
      toast.success('💎 Sponsored Poll live!');
      setForm({ ...form, question: '', options: ['', ''], brand_name: '', brand_logo_url: '', brand_click_url: '', accept_terms: false });
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border-2 border-amber-400/40 bg-gradient-to-br from-amber-950/40 to-slate-900 p-4 space-y-2" data-testid="asn-sponsored-poll">
      <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black">💎 Sponsored Poll · 500 AT (33% cascade)</div>
      <div className="text-[10px] opacity-80">Business market research + brand visibility. Your logo + name appears on the poll for city-wide voters.</div>
      <input placeholder="Brand Name" value={form.brand_name} onChange={(e) => setForm({ ...form, brand_name: e.target.value })} data-testid="spoll-brand" className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
      <div className="flex gap-2">
        <input placeholder="Brand Logo URL (opt)" value={form.brand_logo_url} onChange={(e) => setForm({ ...form, brand_logo_url: e.target.value })} className="flex-1 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
        <UploadPickerBtn value={form.brand_logo_url} onChange={(url) => setForm({ ...form, brand_logo_url: url })} label="Logo" testid="spoll-logo-upload" />
      </div>
      <input placeholder="Brand Click URL (opt)" value={form.brand_click_url} onChange={(e) => setForm({ ...form, brand_click_url: e.target.value })} className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
      <input placeholder="Poll Question" value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} data-testid="spoll-q" className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
      {form.options.map((o, i) => (
        <div key={i} className="flex gap-1">
          <input placeholder={`Option ${i + 1}`} value={o} onChange={(e) => { const opts = [...form.options]; opts[i] = e.target.value; setForm({ ...form, options: opts }); }} className="flex-1 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          {form.options.length > 2 && <button onClick={() => setForm({ ...form, options: form.options.filter((_, j) => j !== i) })} className="px-2 rounded bg-red-500/25 text-red-300 text-xs">✕</button>}
        </div>
      ))}
      <div className="flex gap-1">
        {form.options.length < 6 && <button onClick={() => setForm({ ...form, options: [...form.options, ''] })} className="px-2 py-1 rounded bg-slate-700 text-slate-200 text-[10px]">+ Option</button>}
        <input placeholder="Target City (blank=global)" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="flex-1 px-2 py-1 rounded bg-slate-800 border border-slate-700 text-[11px]" />
        <input type="number" placeholder="Hours" value={form.expires_hours} onChange={(e) => setForm({ ...form, expires_hours: parseInt(e.target.value) || 72 })} className="w-16 px-2 py-1 rounded bg-slate-800 border border-slate-700 text-[11px]" />
      </div>
      <label className="flex items-start gap-2 text-[10px]"><input type="checkbox" checked={form.accept_terms} onChange={(e) => setForm({ ...form, accept_terms: e.target.checked })} data-testid="spoll-tc" className="mt-0.5" /><span>I accept 500 AT deduction is final & poll runs as-is under ASN policy.</span></label>
      <button onClick={submit} disabled={busy || !form.accept_terms} data-testid="spoll-submit" className="w-full px-3 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-yellow-400 text-black text-sm font-black disabled:opacity-40">{busy ? 'Launching…' : 'Pay 500 AT & Launch Sponsored Poll'}</button>
    </div>
  );
}
// ============ ADS (Self-Serve · Plan A/B) ============
function AdsTab({ city, config }) {
  const plans = (config?.fees?.ad_plans) || { A: { code: 'A', label: 'Plan A · 7 Days', days: 7, price_at: 200 }, B: { code: 'B', label: 'Plan B · 30 Days', days: 30, price_at: 500 } };
  const [form, setForm] = useState({ banner_url: '', target_city: city || '', click_url: '', plan: 'A', slot: 'in_feed', referred_by_reporter: '', accept_terms: false });
  const [mine, setMine] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [showStudio, setShowStudio] = useState(false);
  const load = useCallback(async () => {
    try { const r = await api.get('/asn/ads/mine'); setMine(r.data?.ads || []); } catch { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  const submit = async () => {
    if (!form.banner_url || !form.target_city) { toast.error('Banner + city required'); return; }
    if (!form.accept_terms) { toast.error('Terms accept karo'); return; }
    const p = plans[form.plan];
    if (!window.confirm(`${p.price_at} AT deduct hoga (${p.label}) — proceed?`)) return;
    setSubmitting(true);
    try {
      await api.post('/asn/ads', form);
      toast.success(`Ad submitted (${p.label})`);
      setForm({ ...form, banner_url: '', click_url: '', accept_terms: false });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };
  const badge = { PENDING: 'bg-amber-500/25 text-amber-300', APPROVED: 'bg-emerald-500/25 text-emerald-300', REJECTED: 'bg-red-500/25 text-red-300' };

  return (
    <div className="space-y-4" data-testid="asn-ads-tab">
      <div className="rounded-2xl border border-rose-400/30 bg-rose-500/5 p-4 space-y-2">
        <div className="text-xs uppercase tracking-widest text-rose-300 font-black mb-1">📢 Create Self-Serve Ad</div>
        {/* Plan selector */}
        <div className="grid grid-cols-2 gap-2">
          {Object.values(plans).map((p) => (
            <button
              key={p.code} onClick={() => setForm({ ...form, plan: p.code })}
              data-testid={`ad-plan-${p.code}`}
              className={`text-left rounded-xl border-2 p-3 transition-all ${form.plan === p.code ? 'border-amber-400 bg-amber-500/15 shadow-lg shadow-amber-500/30' : 'border-slate-700 bg-slate-800/40 hover:border-amber-400/50'}`}
            >
              <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black">{p.label}</div>
              <div className="text-lg font-black">{p.price_at} AT</div>
              <div className="text-[10px] opacity-70">{p.days} days display · 7-level Affiliate Network cascade</div>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <input placeholder="Banner Image URL (or upload → or design →)" value={form.banner_url} onChange={(e) => setForm({ ...form, banner_url: e.target.value })} data-testid="ad-banner" className="flex-1 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <UploadPickerBtn value={form.banner_url} onChange={(url) => setForm({ ...form, banner_url: url })} label="Banner" testid="ad-banner-upload" />
          <button onClick={() => setShowStudio(true)} data-testid="ad-studio-open" className="text-[10px] px-2 py-1 rounded font-black bg-gradient-to-r from-amber-500 to-yellow-400 text-black">🎨 Design</button>
        </div>
        {showStudio && <AdStudioModal onExport={(url) => setForm({ ...form, banner_url: url })} onClose={() => setShowStudio(false)} />}
        <div className="grid grid-cols-2 gap-2">
          <input placeholder="Target City" value={form.target_city} onChange={(e) => setForm({ ...form, target_city: e.target.value })} data-testid="ad-city" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <input placeholder="Click URL (optional)" value={form.click_url} onChange={(e) => setForm({ ...form, click_url: e.target.value })} data-testid="ad-click" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <select value={form.slot} onChange={(e) => setForm({ ...form, slot: e.target.value })} data-testid="ad-slot" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs">
            <option value="in_feed">In-Feed</option><option value="header">Header Banner</option><option value="footer">Footer Banner</option>
          </select>
          <input placeholder="Ref by Reporter (opt · earns L1 15%)" value={form.referred_by_reporter} onChange={(e) => setForm({ ...form, referred_by_reporter: e.target.value })} data-testid="ad-ref" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
        </div>
        <label className="flex items-start gap-2 text-[10px] cursor-pointer">
          <input type="checkbox" checked={form.accept_terms} onChange={(e) => setForm({ ...form, accept_terms: e.target.checked })} data-testid="ad-tc" className="mt-0.5" />
          <span>I accept ASN Ad Terms, comply with truthful advertising, and the {plans[form.plan]?.price_at} AT deduction is final.</span>
        </label>
        <button onClick={submit} disabled={submitting || !form.accept_terms} data-testid="ad-submit" className="w-full px-3 py-2 rounded-lg bg-gradient-to-r from-rose-500 to-pink-500 text-white text-sm font-black disabled:opacity-40">{submitting ? 'Submitting…' : `Pay ${plans[form.plan]?.price_at} AT & Submit`}</button>
      </div>
      <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-4">
        <div className="text-xs uppercase tracking-widest text-rose-300 font-black mb-2">My Ads ({mine.length})</div>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {mine.map((a) => (
            <div key={a.id} className="rounded-lg border border-slate-700 bg-slate-800/50 p-2 flex items-center gap-2">
              <img src={a.banner_url} alt="ad" className="w-16 h-10 object-cover rounded" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-black truncate">{a.target_city} · Plan {a.plan || '?'} · {a.days}d · {a.price_at} AT</div>
                <div className="flex items-center gap-1 text-[10px] opacity-70">
                  <CountdownBadge expiry={a.display_expiry || a.expires_at} testid={`ad-mine-cd-${a.id}`} />
                  <span>· {a.slot}</span>
                </div>
              </div>
              <span className={`text-[9px] px-2 py-0.5 rounded font-black ${badge[a.status] || badge.PENDING}`}>{a.status}</span>
            </div>
          ))}
          {mine.length === 0 && <div className="text-xs opacity-60 text-center py-4">No ads yet</div>}
        </div>
      </div>

      <SponsoredPollForm />
    </div>
  );
}


// ============ TRUST SCORE ============
function TrustScoreCard({ rep, threshold = 5 }) {
  const score = rep.trust_score || 0;
  const target = threshold;
  const pct = Math.min(100, (score / target) * 100);
  const autoOn = rep.auto_publish_enabled;
  return (
    <div className="rounded-2xl border border-amber-400/40 bg-amber-500/5 p-3" data-testid="asn-trust-card">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-1">
        <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black flex items-center gap-1"><Sparkles className="w-3 h-3" /> Trust Score {autoOn && <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded bg-emerald-500 text-black">✨ AUTO-PUBLISH ON</span>}</div>
        <div className="text-lg font-black text-amber-300">{score}<span className="text-xs opacity-70">/{target}+</span></div>
      </div>
      <div className="h-2 rounded-full bg-slate-800 overflow-hidden mb-1">
        <div className="h-full bg-gradient-to-r from-amber-500 to-yellow-300" style={{ width: `${pct}%` }}></div>
      </div>
      <div className="text-[10px] opacity-80">
        {autoOn ? '🎉 Your content auto-publishes — no admin wait!' : `Get ${Math.max(0, target - score)} more approved posts to unlock instant publishing (each rejection -2)`}
      </div>
    </div>
  );
}

// ============ AD EARNINGS (Reporter Portal · 33% Cascade) ============
function ReporterAdEarnings() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    api.get('/asn/reporter/ad-earnings').then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data) return null;
  return (
    <div className="rounded-2xl border border-rose-400/30 bg-rose-500/5 p-3" data-testid="asn-ad-earnings">
      <button onClick={() => setOpen(!open)} className="w-full text-left flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-rose-300 font-black flex items-center gap-1"><Megaphone className="w-3 h-3" /> Ad Sales Cascade · 33% 7-Level</div>
          <div className="text-[10px] opacity-80">{data.total_direct_referred} direct referrals · Total ad value: {data.total_ad_value_at} AT</div>
        </div>
        <div className="text-right">
          <div className="text-xl font-black text-rose-300">{data.total_earnings_at.toLocaleString()} AT</div>
          <div className="text-[9px] opacity-70">{open ? '▲ Hide' : '▼ Details'}</div>
        </div>
      </button>
      {open && (
        <div className="mt-2 space-y-1 max-h-60 overflow-y-auto">
          {(data.transactions || []).length === 0 ? (
            <div className="text-[11px] opacity-70 text-center py-3">Ad cascade earnings 0 hain. Advertisers ko apna username share karo — 33% Affiliate Network cascade activate hoga!</div>
          ) : (data.transactions || []).map((t) => (
            <div key={t.id} className="flex items-center gap-2 rounded bg-slate-800/60 p-1.5 text-[11px]">
              <div className="flex-1 min-w-0">
                <div className="font-black truncate">{t.description}</div>
                <div className="opacity-70 text-[10px]">{new Date(t.created_at).toLocaleDateString('en-IN')}</div>
              </div>
              <div className="text-right"><div className="font-black text-rose-300">+{t.amount} AT</div></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ HELPLINES CARD ============
function HelplinesCard({ city }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get('/asn/helplines', { params: city ? { city } : {} }).then((r) => setRows(r.data?.helplines || [])).catch(() => {});
  }, [city]);
  if (rows.length === 0) return null;
  return (
    <div className="rounded-2xl border border-red-400/30 bg-red-500/5 p-3" data-testid="asn-helplines">
      <div className="text-[10px] uppercase tracking-widest text-red-300 font-black mb-2 flex items-center gap-1"><Phone className="w-3 h-3" /> Emergency Helplines {city && <span className="opacity-70 normal-case tracking-normal">· {city}</span>}</div>
      <div className="grid grid-cols-2 gap-1.5">
        {rows.map((h, i) => (
          <a key={h.id || i} href={`tel:${h.number}`} className="rounded-lg bg-slate-800/60 border border-slate-700 hover:border-red-400 p-2 flex items-center gap-2 transition-all" data-testid={`helpline-${h.id || i}`}>
            <span className="text-lg">{h.icon || '📞'}</span>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] opacity-70 truncate">{h.category}</div>
              <div className="text-xs font-black truncate">{h.name}</div>
              <div className="text-[10px] font-mono text-red-300">{h.number}</div>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}

// ============ LEADERBOARD TAB ============
function LeaderboardTab() {
  const [board, setBoard] = useState([]);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get('/asn/leaderboard', { params: { days } }).then((r) => setBoard(r.data?.leaderboard || [])).finally(() => setLoading(false));
  }, [days]);
  const rankStyle = (r) => r === 1 ? 'border-yellow-400 bg-gradient-to-br from-yellow-500/30 to-amber-900/30 shadow-lg shadow-yellow-500/40'
    : r === 2 ? 'border-slate-300 bg-gradient-to-br from-slate-400/20 to-slate-800/40'
    : r === 3 ? 'border-orange-400 bg-gradient-to-br from-orange-500/20 to-amber-900/30'
    : 'border-slate-700 bg-slate-900/50';
  return (
    <div data-testid="asn-leaderboard-tab" className="space-y-3">
      <div className="rounded-2xl border-2 border-amber-400/40 bg-gradient-to-br from-amber-950/60 to-slate-900 p-4 relative overflow-hidden">
        <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-yellow-500/20 blur-3xl pointer-events-none"></div>
        <div className="relative flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black flex items-center gap-1"><Trophy className="w-3 h-3" /> Reporter Leaderboard</div>
            <div className="text-lg font-black bg-gradient-to-r from-yellow-300 to-amber-500 bg-clip-text text-transparent">Top 10 Earners</div>
          </div>
          <select value={days} onChange={(e) => setDays(parseInt(e.target.value))} data-testid="leader-days" className="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-xs">
            <option value={7}>Last 7 days</option><option value={30}>Last 30 days</option><option value={90}>Last 90 days</option>
          </select>
        </div>
      </div>
      {loading ? <div className="text-center py-8 opacity-70"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div> : (
        <div className="space-y-2">
          {board.length === 0 ? (
            <div className="rounded-xl border border-slate-700 bg-slate-900/60 p-8 text-center text-sm opacity-70">
              <Trophy className="w-12 h-12 mx-auto opacity-30 mb-2" />
              Abhi tak koi Top 10 nahi hai — banoge tum #1?
            </div>
          ) : board.map((r) => (
            <div key={r.user_id} data-testid={`leader-${r.rank}`} className={`rounded-xl border-2 p-3 flex items-center gap-3 transition-all hover:scale-[1.01] ${rankStyle(r.rank)}`}>
              <div className="text-2xl w-10 text-center font-black">
                {r.rank === 1 ? '🥇' : r.rank === 2 ? '🥈' : r.rank === 3 ? '🥉' : `#${r.rank}`}
              </div>
              {r.photo_url ? <img src={r.photo_url} alt={r.full_name} className="w-12 h-12 rounded-full border-2 border-amber-400 object-cover" onError={(e) => e.currentTarget.style.display = 'none'} /> : <div className="w-12 h-12 rounded-full bg-slate-700 flex items-center justify-center text-lg">👤</div>}
              <div className="flex-1 min-w-0">
                <div className="text-xs font-black truncate">{r.full_name || r.username}</div>
                <div className="text-[10px] opacity-70 truncate">{r.reporter_id} · {r.city || '—'}</div>
                <div className="flex items-center gap-2 text-[9px] opacity-80 mt-0.5">
                  <span className="text-emerald-300">✨ Trust {r.trust_score}</span>
                  <span className="text-blue-300"><Users className="w-2.5 h-2.5 inline" /> {r.active_directs} directs</span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-lg font-black text-amber-300">{r.total_at.toLocaleString()}</div>
                <div className="text-[9px] opacity-70">AT · {r.events} events</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ LIVE TV ============
function LiveTVTab({ city }) {
  const [videos, setVideos] = useState([]);
  const [filterCity, setFilterCity] = useState(city || '');
  const [active, setActive] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await api.get('/asn/live-tv', { params: filterCity ? { city: filterCity } : {} });
      setVideos(r.data?.videos || []);
      if (!active && r.data?.videos?.length) setActive(r.data.videos[0]);
    } catch { /* silent */ }
  }, [filterCity, active]);
  useEffect(() => { load(); }, [load]);

  const ytEmbed = (url) => {
    if (!url) return '';
    const m = url.match(/(?:youtube\.com\/(?:.*v=|embed\/|shorts\/)|youtu\.be\/)([^&?/]+)/);
    return m ? `https://www.youtube.com/embed/${m[1]}?autoplay=1&rel=0` : url;
  };

  return (
    <div data-testid="asn-live-tv">
      <div className="mb-3 flex items-center gap-2 flex-wrap">
        <div className="text-xs uppercase tracking-widest text-red-300 font-black flex items-center gap-1"><Radio className="w-3 h-3 animate-pulse" /> Live TV · Hyperlocal Videos</div>
        <input placeholder="Filter city" value={filterCity} onChange={(e) => setFilterCity(e.target.value)} data-testid="live-tv-city" className="ml-auto px-2 py-1 rounded bg-slate-800 border border-slate-700 text-xs" />
      </div>
      {videos.length === 0 ? (
        <div className="rounded-xl border border-slate-700 bg-slate-900/60 p-8 text-center text-sm opacity-70">
          <Youtube className="w-12 h-12 mx-auto opacity-30 mb-2" />
          Koi live video available nahi. Reporters submit karke daal sakte hain.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2">
            {active && (
              <div className="rounded-xl overflow-hidden border-2 border-red-400/40 bg-black" data-testid="live-tv-player">
                <div className="aspect-video">
                  <iframe title={active.title} src={ytEmbed(active.youtube_url)} allowFullScreen allow="autoplay; encrypted-media" className="w-full h-full" />
                </div>
                <div className="p-3">
                  <div className="text-sm font-black flex items-center gap-2">{active.is_featured && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500 text-black font-black">⭐ FEATURED</span>} {active.title}</div>
                  <div className="text-[10px] opacity-70 mt-0.5">📍 {active.city} · {active.category} · @{active.username} · {new Date(active.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
                  {active.body && <div className="text-xs opacity-90 mt-2 whitespace-pre-wrap line-clamp-4">{active.body}</div>}
                </div>
              </div>
            )}
          </div>
          <div className="space-y-2 max-h-[540px] overflow-y-auto">
            <div className="text-[10px] uppercase tracking-widest text-red-300 font-black">Up Next ({videos.length})</div>
            {videos.map((v) => (
              <button key={v.id} onClick={() => setActive(v)} data-testid={`live-tv-item-${v.id}`}
                className={`w-full text-left rounded-lg border p-2 transition-all ${active?.id === v.id ? 'border-red-400 bg-red-500/15' : 'border-slate-700 bg-slate-800/50 hover:border-red-400/50'}`}>
                <div className="text-xs font-black truncate flex items-center gap-1">{v.is_featured && <span className="text-amber-300" title="Featured by Admin">⭐</span>} {v.title}</div>
                <div className="text-[10px] opacity-70">{v.city} · {v.category}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ============ WEEKLY PAYOUT REPORT ============
function WeeklyPayoutCard({ rep }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get('/asn/reporter/weekly-payout', { params: { days: 7 } });
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const downloadPdf = async () => {
    if (!data) return;
    try {
      const jsPDF = (await import('jspdf')).default;
      const doc = new jsPDF({ unit: 'pt', format: 'a4' });
      const W = doc.internal.pageSize.getWidth();
      let y = 40;
      doc.setFillColor(15, 30, 78); doc.rect(0, 0, W, 80, 'F');
      doc.setTextColor(255, 215, 90); doc.setFont('helvetica', 'bold'); doc.setFontSize(20);
      doc.text('ANANT SAMACHAR NETWORK', W / 2, 32, { align: 'center' });
      doc.setFontSize(11); doc.setTextColor(255, 240, 200);
      doc.text('Weekly Payout Report (7 days)', W / 2, 54, { align: 'center' });
      y = 110;
      doc.setTextColor(20, 20, 20); doc.setFontSize(10); doc.setFont('helvetica', 'normal');
      doc.text(`Reporter: ${data.reporter?.full_name || '—'} (@${data.reporter?.username || '—'})`, 40, y); y += 14;
      doc.text(`ID: ${data.reporter?.reporter_id || '—'} · Mobile: +91 ${data.reporter?.mobile || '—'} · ${data.reporter?.city || '—'}`, 40, y); y += 14;
      doc.text(`Period: since ${new Date(data.since).toLocaleDateString('en-IN')} to ${new Date().toLocaleDateString('en-IN')}`, 40, y); y += 20;

      // Grand total
      doc.setFillColor(200, 162, 74); doc.rect(40, y, W - 80, 40, 'F');
      doc.setTextColor(15, 30, 78); doc.setFontSize(14); doc.setFont('helvetica', 'bold');
      doc.text(`GRAND TOTAL: ${data.grand_total_at.toLocaleString()} AT`, W / 2, y + 26, { align: 'center' });
      y += 60;

      // Ad section
      doc.setTextColor(20, 20, 20); doc.setFontSize(11); doc.setFont('helvetica', 'bold');
      doc.text(`📢 Ad Sales Commissions (${data.ad_commissions.commission_pct}%)`, 40, y); y += 14;
      doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
      doc.text(`Total: ${data.ad_commissions.total_at.toLocaleString()} AT   ·   ${data.ad_commissions.count} ads referred`, 40, y); y += 16;
      (data.ad_commissions.items || []).slice(0, 10).forEach((a) => {
        doc.text(`• ${new Date(a.created_at).toLocaleDateString('en-IN')} · ${a.target_city} · +${a.reporter_credit_at} AT`, 50, y); y += 12;
      });
      y += 8;

      // MLM section
      doc.setFont('helvetica', 'bold'); doc.setFontSize(11);
      doc.text('🎯 7-Level Affiliate Network Commissions', 40, y); y += 14;
      doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
      doc.text(`Total: ${data.mlm_commissions.total_at.toLocaleString()} AT   ·   ${data.mlm_commissions.count} events`, 40, y); y += 14;
      Object.entries(data.mlm_commissions.level_breakdown || {}).forEach(([lvl, amt]) => {
        doc.text(`  ${lvl}: ${amt.toLocaleString()} AT`, 50, y); y += 12;
      });

      // Footer
      doc.setFontSize(8); doc.setTextColor(120, 120, 120);
      doc.text('This is an auto-generated report. AT credits already reflected in your wallet.', W / 2, 800, { align: 'center' });
      doc.save(`ASN-Weekly-Payout-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success('PDF downloaded');
    } catch (e) { toast.error('PDF generation failed'); }
  };

  const shareWhatsApp = () => {
    if (!data) return;
    const msg = [
      `📰 *ASN Weekly Payout* (7 days)`,
      `Reporter: ${data.reporter?.full_name || '—'} (@${data.reporter?.username || '—'})`,
      `ID: ${data.reporter?.reporter_id || '—'}`,
      ``,
      `💰 *Grand Total: ${data.grand_total_at.toLocaleString()} AT*`,
      ``,
      `📢 Ad Commissions: ${data.ad_commissions.total_at.toLocaleString()} AT (${data.ad_commissions.count} ads · ${data.ad_commissions.commission_pct}%)`,
      `🎯 Affiliate Network: ${data.mlm_commissions.total_at.toLocaleString()} AT (${data.mlm_commissions.count} events)`,
      ``,
      `Anant Samachar Network — Truth. Clarity. Infinite.`,
    ].join('\n');
    const url = `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(url, '_blank');
  };

  if (!data) return null;
  return (
    <div className="rounded-2xl border border-emerald-400/40 bg-gradient-to-br from-emerald-950/40 to-slate-900 p-3" data-testid="asn-weekly-payout">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
        <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-black flex items-center gap-1"><FileText className="w-3 h-3" /> Weekly Payout Report (7 days)</div>
        <div className="text-lg font-black text-emerald-300">{data.grand_total_at.toLocaleString()} AT</div>
      </div>
      <div className="grid grid-cols-2 gap-2 mb-2">
        <div className="rounded-lg bg-rose-500/10 border border-rose-400/30 p-2">
          <div className="text-[9px] uppercase text-rose-300 font-black opacity-80">Ad Commissions</div>
          <div className="text-sm font-black text-rose-200">{data.ad_commissions.total_at.toLocaleString()} AT</div>
          <div className="text-[9px] opacity-70">{data.ad_commissions.count} ads</div>
        </div>
        <div className="rounded-lg bg-emerald-500/10 border border-emerald-400/30 p-2">
          <div className="text-[9px] uppercase text-emerald-300 font-black opacity-80">Affiliate Network</div>
          <div className="text-sm font-black text-emerald-200">{data.mlm_commissions.total_at.toLocaleString()} AT</div>
          <div className="text-[9px] opacity-70">{data.mlm_commissions.count} events</div>
        </div>
      </div>
      <div className="flex gap-2">
        <button onClick={downloadPdf} disabled={busy} data-testid="asn-payout-pdf" className="flex-1 px-2 py-1.5 rounded-lg bg-gradient-to-r from-amber-400 to-yellow-300 text-black text-xs font-black flex items-center justify-center gap-1"><Download className="w-3 h-3" /> Download PDF</button>
        <button onClick={shareWhatsApp} data-testid="asn-payout-wa" className="flex-1 px-2 py-1.5 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs font-black flex items-center justify-center gap-1"><MessageCircle className="w-3 h-3" /> WhatsApp Share</button>
      </div>
    </div>
  );
}

// ============ KYC OCR HELPER (Aadhaar / PAN Auto-Fill) ============
function KycOcrHelper({ form, setForm }) {
  const [docType, setDocType] = useState('AADHAAR');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const runOcr = async () => {
    if (!form.kyc_doc_url) { toast.error('KYC document URL upload karo pehle'); return; }
    setBusy(true); setResult(null);
    try {
      const r = await api.post('/asn/kyc/ocr-verify', {
        doc_url: form.kyc_doc_url, doc_type: docType,
        selfie_url: form.photo_url || undefined,
      });
      const ext = r.data?.extracted || {};
      const face = r.data?.face_match || null;
      const patch = {};
      if (ext.full_name && !form.full_name) patch.full_name = ext.full_name;
      if (Object.keys(patch).length) setForm({ ...form, ...patch });
      setResult({ extracted: ext, face_match: face });
      toast.success(`OCR done · confidence ${ext.confidence ?? '—'}%${face ? ` · face ${face.similarity ?? 0}%` : ''}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'OCR failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-emerald-400/30 bg-emerald-500/5 p-2" data-testid="asn-kyc-ocr">
      <div className="flex items-center gap-2 flex-wrap">
        <select value={docType} onChange={(e) => setDocType(e.target.value)} data-testid="asn-ocr-doctype" className="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-[10px]">
          <option value="AADHAAR">Aadhaar</option><option value="PAN">PAN</option>
        </select>
        <button
          onClick={runOcr} disabled={busy || !form.kyc_doc_url}
          data-testid="asn-ocr-run"
          className={`flex-1 px-2 py-1.5 rounded text-[10px] font-black flex items-center justify-center gap-1 ${busy ? 'bg-amber-500/40 text-amber-200' : 'bg-emerald-500 text-black hover:bg-emerald-400 disabled:opacity-40'}`}
        >
          {busy ? '⏳ AI scanning…' : `🤖 Auto-fill from ${docType} (AI OCR + Face Match)`}
        </button>
      </div>
      {result && (
        <div className="mt-2 text-[10px] space-y-0.5" data-testid="asn-ocr-result">
          <div><b className="text-emerald-300">Name:</b> {result.extracted.full_name || '—'}</div>
          {result.extracted.dob && <div><b className="text-emerald-300">DOB:</b> {result.extracted.dob}</div>}
          {(result.extracted.aadhaar_number || result.extracted.pan_number) && (
            <div><b className="text-emerald-300">{docType} No:</b> <span className="font-mono">{result.extracted.aadhaar_number || result.extracted.pan_number}</span></div>
          )}
          <div><b className="text-emerald-300">Confidence:</b> {result.extracted.confidence ?? '—'}%</div>
          {result.face_match && (
            <div className={result.face_match.same_person ? 'text-emerald-300' : 'text-amber-300'}>
              <b>Face Match:</b> {result.face_match.similarity}% · {result.face_match.same_person ? '✅ Same person' : '⚠️ Verify manually'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

